import * as braze from '@braze/web-sdk';
import { isServer } from 'utils/misc';
import { UserData } from '../stores/AuthStore';

type InitProps = {
  apiKey: string;
  baseUrl: string;
  enableLogging: boolean;
  enableIam: boolean;
};

type PendingPromise = {
  fn: () => Promise<void>;
  resolve: (value: void) => void;
  reject: (error: Error) => void;
};

export type BrazeUserProps = Pick<
  UserData['dataUser'],
  'id' | 'firstName' | 'lastName' | 'email' | 'phoneHome' | 'billing'
>;

type LogPurchasePayload = {
  productId: string;
  price: number;
  currency?: string;
  quantity: number;
  discountApplied?: boolean;
  paymentMethod?: string;
  eventName?: string;
  eventId?: string;
  eventCategory?: string;
  eventCity?: string;
};

export default class BrazeService {
  _brazeInitialized = false;
  _iamInitialized = false;
  private _pendingPromises: PendingPromise[] = [];

  init({ apiKey, baseUrl, enableLogging, enableIam }: InitProps) {
    if (isServer() || this._brazeInitialized) {
      return;
    }

    if (!apiKey || !baseUrl) {
      throw new Error('[BrazeService] missing config');
    }

    braze.initialize(apiKey, {
      baseUrl,
      enableLogging,
      serviceWorkerLocation: '/discovery-next-service-worker.js'
    });
    this._brazeInitialized = true;

    if (enableIam && !this._iamInitialized) {
      try {
        this._initIam();
        this._iamInitialized = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[BrazeService] `_initIam`', error);
        this._iamInitialized = false;
      }
    }

    this._flushPendingPromises().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('[BrazeService] _flushPendingPromises', error);
    });

    braze.openSession();
  }

  destroy() {
    if (isServer() || !this._brazeInitialized) {
      return;
    }

    braze.destroy();
  }

  flushDeferredIam() {
    if (!this._brazeInitialized || !this._iamInitialized) return;

    const deferredIam = braze.getDeferredInAppMessage();
    if (!deferredIam) return;

    this._handleIam(deferredIam);
  }

  _shouldDeferIam() {
    // Example condition: defer IAMs during checkout process
    // if (isCheckout) return true;

    return false;
  }

  _isPushPrimerIam(inAppMessage: braze.InAppMessage) {
    const keyValuePairs = inAppMessage?.extras ?? {};
    return keyValuePairs['msg-id'] === 'push-primer';
  }

  _handlePushPrimerIam(inAppMessage: braze.InAppMessage) {
    const pushPrimerModal = inAppMessage as braze.ModalMessage;
    const shouldShowPushPrimer =
      braze.isPushSupported() &&
      !braze.isPushBlocked() &&
      !braze.isPushPermissionGranted();
    if (!shouldShowPushPrimer) return;

    const acceptButton = pushPrimerModal?.buttons?.[0];
    if (acceptButton) {
      acceptButton.subscribeToClickedEvent(() => {
        braze.requestPushPermission(
          // eslint-disable-next-line no-console
          () => console.debug('[BrazeService] requestPushPermission accepted'),
          // eslint-disable-next-line no-console
          () => console.debug('[BrazeService] requestPushPermission denied')
        );
      });
    }

    braze.showInAppMessage(pushPrimerModal);
  }

  _handleIam(inAppMessage: braze.InAppMessage | braze.ControlMessage) {
    if (inAppMessage.isControl) {
      braze.showInAppMessage(inAppMessage);
      return;
    }

    if (this._shouldDeferIam()) {
      braze.deferInAppMessage(inAppMessage);
      return;
    }

    if (this._isPushPrimerIam(inAppMessage)) {
      this._handlePushPrimerIam(inAppMessage);
      return;
    }

    braze.showInAppMessage(inAppMessage);
  }

  _initIam() {
    braze.subscribeToInAppMessage(this._handleIam.bind(this));
  }

  private async _flushPendingPromises() {
    if (this._pendingPromises.length === 0) return;

    const pending = [...this._pendingPromises];
    this._pendingPromises = [];

    for (const { fn, resolve, reject } of pending) {
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error as Error);
      }
    }
  }

  private _queue(fn: () => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this._brazeInitialized) {
        try {
          resolve(fn());
        } catch (error) {
          reject(error);
        }
      } else {
        this._pendingPromises.push({ fn, resolve, reject });
      }
    });
  }

  public logCustomEvent(
    eventName: string,
    payload?: Record<string, unknown>
  ): Promise<void> {
    return this._queue(async () => {
      braze.logCustomEvent(eventName, payload);
    });
  }

  public identifyUser(user?: BrazeUserProps) {
    if (!user) {
      return Promise.resolve();
    }

    if (typeof user.id !== 'number') {
      // eslint-disable-next-line no-console
      console.warn('[BrazeService] Invalid user id');
      return Promise.resolve();
    }

    return this._queue(async () => {
      braze.changeUser(String(user.id));
      braze.getUser()?.setFirstName(user.firstName);
      braze.getUser()?.setLastName(user.lastName);
      braze.getUser()?.setEmail(user.email);
      braze.getUser()?.setPhoneNumber(user.phoneHome);
      braze.getUser()?.setHomeCity(user.billing?.addressCity ?? null);
    });
  }

  public logPurchase(payload: LogPurchasePayload) {
    if (!payload.productId || typeof payload.productId !== 'string') {
      // eslint-disable-next-line no-console
      console.warn('[BrazeService] Invalid productId');
      return Promise.resolve();
    }

    return this._queue(async () => {
      braze.logPurchase(
        payload.productId,
        payload.price || 0,
        payload.currency || 'BRL',
        payload.quantity || 1,
        {
          discount_applied: payload.discountApplied,
          payment_method: payload.paymentMethod,
          event_name: payload.eventName,
          event_id: payload.eventId,
          event_category: payload.eventCategory,
          city: payload.eventCity
        }
      );
    });
  }
}

export const brazeService = new BrazeService();
